import { fmtBRL } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Package, Truck, CheckCircle2, Ban, RotateCcw, CalendarClock, DollarSign, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { fmtDateBR } from "./dashboardFilters";

function TrendChip({ cur, prev, invert, testId }) {
  const c = Number(cur) || 0, p = Number(prev) || 0;
  let pct = null, dir = 0;
  if (p === 0 && c === 0) { dir = 0; }
  else if (p === 0) { dir = 1; }
  else { pct = ((c - p) / p) * 100; dir = pct > 0 ? 1 : pct < 0 ? -1 : 0; }
  const good = dir === 0 ? null : (invert ? dir < 0 : dir > 0);
  const cls = dir === 0 ? "bg-slate-100 text-slate-600" : good ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700";
  const Icon = dir > 0 ? ArrowUpRight : dir < 0 ? ArrowDownRight : Minus;
  const label = dir === 0 ? "0%" : pct === null ? "novo" : `${pct > 0 ? "+" : ""}${Math.abs(pct) >= 100 ? pct.toFixed(0) : pct.toFixed(1)}%`;
  return (
    <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${cls}`}
      title={`Período anterior: ${prev}`} data-testid={testId}>
      <Icon className="w-3 h-3" />{label}
    </span>
  );
}

function StatCard({ label, value, icon: Icon, color, testId, accent, trend }) {
  const accentCls = accent === "positive" ? "border-emerald-300 bg-emerald-50/60" : accent === "negative" ? "border-rose-300 bg-rose-50/60" : "";
  const valueCls = accent === "positive" ? "text-emerald-700" : accent === "negative" ? "text-rose-700" : "";
  return (
    <Card data-testid={testId} className={accentCls}>
      <CardContent className="p-4 flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
          <div className={`text-2xl md:text-3xl font-extrabold mt-1 ${valueCls}`}>{value}</div>
          {trend && (
            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
              <TrendChip cur={trend.cur} prev={trend.prev} invert={trend.invert} testId={`${testId}-trend`} />
              <span className="text-[10px] text-muted-foreground">vs {trend.prevLabel}</span>
            </div>
          )}
        </div>
        <div className={`${color} p-3 rounded-xl text-white shrink-0`}><Icon className="w-5 h-5" /></div>
      </CardContent>
    </Card>
  );
}

// Iter 96: total = concluídas + remarcadas + canceladas + pendentes (aguardando) + em trânsito
const sumTotal = (bs) => ["concluida", "remarcar", "cancelada", "aguardando", "em_transito"].reduce((acc, k) => acc + (bs?.[k] || 0), 0);

// Iter 93: cards (contagens + ticket médio). Comparativo `s.previous` vira seta verde/vermelha.
export function buildStatCards(s, canSeeFinanceiro) {
  const prev = s.previous;
  const prevLabel = prev ? `${fmtDateBR(prev.period.start)}–${fmtDateBR(prev.period.end)}` : "";
  const tr = (cur, prevVal, invert = false) => (prev ? { cur, prev: prevVal, invert, prevLabel } : null);
  const bs = s.by_status, pbs = prev?.by_status || {};
  const countCards = [
    { label: "Total de Entregas", value: sumTotal(bs), icon: Package, color: "bg-orange-600", tid: "stat-total", trend: tr(sumTotal(bs), sumTotal(pbs)) },
    { label: "Entregas Concluídas", value: bs.concluida || 0, icon: CheckCircle2, color: "bg-emerald-500", tid: "stat-concluida", trend: tr(bs.concluida || 0, pbs.concluida) },
    { label: "Entregas Canceladas", value: bs.cancelada || 0, icon: Ban, color: "bg-rose-500", tid: "stat-cancelada", trend: tr(bs.cancelada || 0, pbs.cancelada, true) },
    { label: "Em Trânsito", value: bs.em_transito || 0, icon: Truck, color: "bg-blue-500", tid: "stat-em-transito", trend: tr(bs.em_transito || 0, pbs.em_transito) },
  ];
  const tailCards = [
    { label: "Entregas Remarcadas", value: bs.remarcar || 0, icon: RotateCcw, color: "bg-yellow-500", tid: "stat-remarcar", trend: tr(bs.remarcar || 0, pbs.remarcar, true) },
    { label: "Entregas Pendentes", value: bs.aguardando || 0, icon: CalendarClock, color: "bg-orange-500", tid: "stat-pendentes", trend: tr(bs.aguardando || 0, pbs.aguardando, true) },
  ];
  if (!canSeeFinanceiro) return [...countCards, ...tailCards];
  return [
    ...countCards,
    { label: "Ticket Médio", value: fmtBRL(s.ticket_medio || 0), icon: TrendingUp, color: "bg-indigo-500", tid: "stat-ticket", trend: tr(s.ticket_medio || 0, prev?.ticket_medio) },
    { label: "Faturamento (Concluídas)", value: fmtBRL(s.faturamento_concluidas || s.faturamento || 0), icon: DollarSign, color: "bg-emerald-600", tid: "stat-faturamento", accent: "positive", trend: tr(s.faturamento_concluidas || 0, prev?.faturamento_concluidas) },
    { label: "Valor Perdido", value: fmtBRL(s.valor_perdido || 0), icon: TrendingDown, color: "bg-rose-600", tid: "stat-valor-perdido", accent: "negative", trend: tr(s.valor_perdido || 0, prev?.valor_perdido, true) },
    ...tailCards,
  ];
}

export function DashboardStatCards({ s, canSeeFinanceiro, onApplyPeriod }) {
  const cards = buildStatCards(s, canSeeFinanceiro);
  return (
    <>
      {!s.previous && (
        <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground -mb-1" data-testid="trend-hint">
          <span>Defina um período (De/Até) para ver a variação vs. período anterior:</span>
          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => onApplyPeriod("7d")} data-testid="quick-period-7">Últimos 7 dias</Button>
          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => onApplyPeriod("mes")} data-testid="quick-period-30">Este mês</Button>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
        {cards.map((c) => <StatCard key={c.label} label={c.label} value={c.value} icon={c.icon} color={c.color} testId={c.tid} accent={c.accent} trend={c.trend} />)}
      </div>
    </>
  );
}
